/**
 * Application-wide constants.
 *
 * @remarks
 * Centralized location for all app-level strings, messages, headers, and button labels
 * to ensure consistent copy across the application and ease maintenance.
 */

// ─── Version Check Feature ────────────────────────────────────────────────

export const VERSION_CHECK_MESSAGES = {
  UPDATE_REQUIRED: 'Update is required. Please update your app to continue.',
  UPDATE_AVAILABLE: 'Update is available. Please update your app to continue!',
};

export const VERSION_CHECK_HEADERS = {
  WARNING: 'Warning',
};

export const VERSION_CHECK_BUTTONS = {
  UPDATE: 'Update',
  NOT_NOW: 'Not Now',
};

// ─── Login ───────────────────────────────────────────────────────────────────

/** Toast and inline error messages shown on the login screen. */
export const LOGIN_MESSAGES = {
  SESSION_EXPIRED:         'Session expired. Please sign in again.',
  SWITCH_BLOCKED:          'Cannot switch user — unsent submissions exist. Please sign in and upload everything first.',
  SWITCH_CONFIRM_BODY:     'Your unsent data will be preserved and uploaded automatically when the original user signs in again. Do you want to proceed?',
  ERR_INVALID_CREDENTIALS: 'Incorrect username or password. Please try again.',
  ERR_SUSPENDED:           'Your account has been suspended. Contact support.',
  ERR_NETWORK:             'Network error. Check your connection and try again.',
  ERR_GENERIC:             'Login failed. Please try again later.',
} as const;

/** Alert headers used on the login screen. */
export const LOGIN_HEADERS = {
  SWITCH_CONFIRM: 'Unsent submissions exist',
} as const;

/** Alert / action-sheet button labels used on the login screen. */
export const LOGIN_BUTTONS = {
  CANCEL:      'Cancel',
  SWITCH_USER: 'Switch user',
} as const;

// ─── Affidavit — Geocoding ──────────────────────────────────────────────────

/**
 * Max time (ms) to wait for the Google Maps Geocoding API in
 * `AffidavitComponent.getAddress()` before giving up and treating the
 * request as failed (2026-08-04 fix, moved here from a local component
 * const 2026-08-05).
 *
 * @remarks
 * `CapacitorHttp.get()` was previously called with no `connectTimeout`/
 * `readTimeout` at all, so the effective wait was whatever platform default
 * `CapacitorHttp` falls back to — confirmed by reading the installed
 * Capacitor packages: native iOS defaults to 600,000 ms (10 min,
 * `HttpRequestHandler.swift`'s `connectTimeout ?? readTimeout ?? 600000.0`),
 * native Android has NO timeout at all (`HttpRequestHandler.java` only calls
 * `setConnectTimeout`/`setReadTimeout` when the option is non-null, so
 * `HttpURLConnection` keeps its own default of `0` = infinite), and the
 * web/PWA fallback (`CapacitorHttpPluginWeb`) is a plain `fetch()` with no
 * `AbortController` at all. A stalled request on any of these could leave
 * "Apply signature" stuck on "Fetching location…" indefinitely with no
 * feedback, since `AffidavitComponent.loadDocument()`'s GPS/geocoding
 * `try/catch` only handles a *rejection*, not a hang.
 *
 * Neither `connectTimeout`/`readTimeout` (native-only) nor an
 * `AbortController` signal via `webFetchExtra` (web-only) is sufficient
 * ALONE to bound this call on every platform, so each is applied as a
 * genuine best-effort cancel of the underlying request **on the platform that
 * actually honours it** — `connectTimeout`/`readTimeout` always,
 * `webFetchExtra` only when `PlatformService.isNative` is `false`. Both are
 * backed by a `Promise.race` against a plain `setTimeout` — the same JS-level
 * safety-net pattern `AuthService.getCoordinates()` already uses for its own
 * (still file-local) `GEO_JS_TIMEOUT_MS` — as the mechanism that's actually
 * guaranteed to stop this method waiting regardless of platform.
 *
 * **The `webFetchExtra` platform guard is mandatory, not an optimization:**
 * sending it on native throws `DataCloneError` from the Capacitor bridge before
 * any request is made, because an `AbortSignal` is not structured-cloneable.
 * See `AffidavitComponent.getAddress()`'s own `@remarks` for the full trace —
 * an earlier version passed it unconditionally and broke affidavit signing on
 * device entirely (2026-08-05 fix).
 */
export const GEOCODE_TIMEOUT_MS = 10000;

// ─── Attempt Log — Note Generation ─────────────────────────────────────────

/**
 * Sentence-fragment templates used by `NoteGeneratorService` to compose the
 * live-generated Attempt Log notes text. `{{token}}` placeholders are
 * substituted with resolved field values (spoke-with relation, bad-address
 * reason, etc.) before punctuation cleanup.
 */
export const ATTEMPT_LOG_NOTE_TEMPLATES = {
  OBSERVED_TEMPLATE: ' I observed: {{obServations}}.',
  SPOKE_TEMPLATE: 'I spoke with {{spokeWithRelation}}, {{spokeWithFirstName}}.',
  LEFT_WITH_TEMPLATE: 'I left papers with {{leftWithRelation}}, {{leftWithFirstName}}.',
  BAD_ADDRESS_TEMPLATE: '{{badAddress}}.',
  BAD_ADDRESS_SPOKE_TEMPLATE: '{{badAddress}}. I spoke with {{spokeWithRelation}}, {{spokeWithFirstName}}.',
} as const;
